import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import AppShell from "@/components/layout/AppShell";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogClose } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import api, { BACKEND_URL } from "@/lib/api";
import { ArrowLeft, Calendar, Newspaper, X as XIcon, Share2, Link2, Copy, Check, Facebook, ChevronDown } from "lucide-react";
import { FlyerImage } from "@/components/PlayerProfileCard";

/* ── helpers ────────────────────────────────────────────── */
function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); } catch { return ""; }
}

function NewsKindBadge({ kind }) {
  if (!kind || kind === "news") return null;
  const map = { announcement: { label: "Announcement", bg: "#dc2626", fg: "#fff" }, community: { label: "Community", bg: "#059669", fg: "#fff" } };
  const s = map[kind] || { label: kind, bg: "#6b7280", fg: "#fff" };
  return <span className="inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: s.bg, color: s.fg }}>{s.label}</span>;
}

function NewsKindStraddleBadge({ kind, itemId }) {
  if (!kind || kind === "news") return null;
  const map = { announcement: { label: "Announcement", bg: "#dc2626", fg: "#fff" }, community: { label: "Community", bg: "#059669", fg: "#fff" } };
  const s = map[kind] || { label: kind, bg: "#6b7280", fg: "#fff" };
  return (
    <span className="absolute bottom-0 left-2 translate-y-1/2 z-10 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full shadow" style={{ background: s.bg, color: s.fg }} data-testid={`news-kind-badge-${itemId}`}>{s.label}</span>
  );
}

function ShareButton({ item }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/api/share/news/${item.id}`;
  const copy = () => { navigator.clipboard.writeText(url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }); };
  const fbShare = () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, "_blank", "width=600,height=400");
  return (
    <div className="flex items-center gap-2 mt-2">
      <button onClick={fbShare} className="p-1.5 rounded-full hover:bg-muted transition-colors" aria-label="Share on Facebook" data-testid="all-news-share-fb"><Facebook className="h-4 w-4" /></button>
      <button onClick={copy} className="p-1.5 rounded-full hover:bg-muted transition-colors" aria-label="Copy link" data-testid="all-news-share-copy">{copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}</button>
    </div>
  );
}

const BATCH = 12; // cards to show per batch

/* ── main page ──────────────────────────────────────────── */
export default function AllNews() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [visible, setVisible] = useState(BATCH);
  const [searchParams] = useSearchParams();
  const isMember = searchParams.get("from") === "member";
  const backTo = isMember ? "/m" : "/";
  const Layout = isMember ? AppShell : PublicLayout;
  const layoutProps = isMember ? { variant: "member", title: "All News" } : {};
  const sentinelRef = React.useRef(null);

  useEffect(() => {
    api.get("/news/public/all")
      .then(({ data }) => setNews(data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Infinite scroll — load more when sentinel enters viewport
  useEffect(() => {
    if (loading || visible >= news.length) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible((v) => Math.min(v + BATCH, news.length)); },
      { rootMargin: "200px" }
    );
    const el = sentinelRef.current;
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); };
  }, [loading, visible, news.length]);

  return (
    <Layout {...layoutProps}>
      <div className="min-h-screen bg-[#faf8f5]" data-testid="all-news-page">
        {/* Header */}
        <div className="w-full" style={{ background: "linear-gradient(135deg, #1E3A8A 0%, #2749C9 55%, #4169E1 100%)" }}>
          <div className="mx-auto max-w-[1920px] px-5 sm:px-6 lg:px-[232px] py-6 sm:py-10 flex items-end justify-between gap-4">
            <div>
              <Link to={backTo} className="inline-flex items-center gap-1.5 text-white/70 text-sm font-medium hover:text-white transition-colors mb-3" data-testid="all-news-back">
                <ArrowLeft className="h-4 w-4" /> Back
              </Link>
              <h1
                className="text-3xl sm:text-5xl lg:text-6xl tracking-tight uppercase leading-none text-white"
                style={{ fontFamily: "'Barlow Condensed', 'Inter', sans-serif", fontWeight: 900 }}
                data-testid="all-news-heading"
              >
                All News
              </h1>
              <p className="text-white/70 text-sm mt-1">Last 30 days</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="mx-auto max-w-[1920px] px-5 sm:px-6 lg:px-[232px] py-8">
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4" data-testid="all-news-loading">
              {Array.from({ length: 12 }).map((_, i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="w-full aspect-[1080/1350] rounded-sm" />
                  <Skeleton className="h-3 w-3/4" />
                  <Skeleton className="h-2 w-1/2" />
                </div>
              ))}
            </div>
          ) : news.length === 0 ? (
            <div className="text-center py-20" data-testid="all-news-empty">
              <Newspaper className="h-16 w-16 mx-auto text-muted-foreground/40 mb-4" />
              <h2 className="text-xl font-bold text-foreground/80">No news in the last 30 days</h2>
              <p className="text-sm text-muted-foreground mt-1">Check back soon for updates.</p>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap gap-4" data-testid="all-news-grid">
                {news.slice(0, visible).map((n) => (
                  <Card
                    key={n.id}
                    className="overflow-hidden rounded-sm border border-neutral-300/90 cursor-pointer transition-[transform,box-shadow] duration-200 ease-out hover:scale-[1.02] hover:-translate-y-2 hover:shadow-[0_20px_44px_-8px_rgba(0,0,0,0.22),0_8px_18px_-4px_rgba(0,0,0,0.12)] hover:z-10 flex flex-col shadow-[0_4px_16px_-4px_rgba(0,0,0,0.15),0_2px_6px_-2px_rgba(0,0,0,0.1),0_0_0_0.5px_rgba(0,0,0,0.04)]"
                    style={{ width: 170 }}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelected(n)}
                    data-testid={`all-news-card-${n.id}`}
                  >
                    <div className="relative bg-white w-full" style={{ aspectRatio: n.image_orientation === "landscape" ? "1200 / 630" : "1080 / 1350" }}>
                      {n.image_url ? (
                        <img src={n.image_url} alt={n.title} className="absolute inset-0 w-full h-full object-contain block" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground bg-[hsl(var(--secondary))]"><Newspaper className="h-5 w-5 opacity-50" /></div>
                      )}
                      <NewsKindStraddleBadge kind={n.kind} itemId={n.id} />
                    </div>
                    <div className="px-2 pt-3 pb-1.5 shrink-0 bg-[#faf8f5]">
                      <h3 className="text-xs font-bold leading-tight truncate text-black" title={n.title}>{n.title}</h3>
                      <div className="mt-0.5 flex items-center gap-1 text-[9px] text-black/70">
                        <Calendar className="h-2.5 w-2.5 shrink-0" /><span>{fmtDate(n.published_at)}</span>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
              {visible < news.length && (
                <div ref={sentinelRef} className="flex justify-center py-8" data-testid="all-news-load-more">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <ChevronDown className="h-4 w-4 animate-bounce" />
                    <span>Loading more…</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Detail dialog */}
        <Dialog open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
          <DialogContent
            className="p-0 w-[98vw] max-w-[98vw] sm:w-auto sm:max-w-[80vw] max-h-[98dvh] overflow-hidden rounded-xl border-0 shadow-2xl bg-white text-foreground [&>button]:hidden"
            data-testid="all-news-detail-dialog"
          >
            {selected && (
              <>
                <DialogClose className="absolute top-2 right-2 z-50 p-1 bg-white/80 backdrop-blur-sm rounded-full sm:hidden" aria-label="Close">
                  <XIcon className="h-7 w-7 text-black" strokeWidth={3} />
                </DialogClose>
                <div className="flex flex-col max-h-[98dvh] sm:max-h-[95dvh] p-2 sm:p-4 cursor-pointer" onClick={() => setSelected(null)}>
                  {selected.image_url ? (
                    selected.source_kind === "team_selection" && selected.player_hotspots?.length > 0 ? (
                      <FlyerImage item={selected} className="max-w-full max-h-[60dvh] object-contain rounded-lg sm:mx-auto sm:block sm:max-h-[75dvh]" />
                    ) : (
                      <img
                        src={selected.image_url}
                        alt={selected.title}
                        className="max-w-full max-h-[60dvh] object-contain rounded-lg sm:mx-auto sm:block sm:max-h-[75dvh]"
                        data-testid="all-news-detail-image"
                      />
                    )
                  ) : (
                    <div className="flex items-center justify-center h-[min(200px,30dvh)] text-muted-foreground"><Newspaper className="h-16 w-16 opacity-50" /></div>
                  )}
                  <div className="relative flex-shrink-0 sm:flex-shrink overflow-y-auto scrollbar-hide sm:max-h-[30dvh]">
                    <div className="p-3 sm:p-4 space-y-2.5">
                      <NewsKindBadge kind={selected.kind} />
                      <h1 className="text-xl sm:text-2xl font-bold tracking-tight leading-tight" style={{ fontFamily: "'Inter', sans-serif", color: "#000000" }} data-testid="all-news-detail-title">{selected.title}</h1>
                      <div className="text-sm text-muted-foreground">{fmtDate(selected.published_at)}</div>
                      {selected.summary && <p className="text-sm leading-relaxed text-foreground/80" data-testid="all-news-detail-summary">{selected.summary}</p>}
                      <ShareButton item={selected} />
                    </div>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
